import * as React from 'react';
import { observer } from 'mobx-react-lite';
import { Popover } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import type { SessionCreationReason } from '../../../common/services/types';
import { useAppStore } from '../../stores';
import { ContractActorTimestampDto } from '../../history/types';
import {
    contractActorName,
    contractLastUpdated,
    formatContractTimestamp,
    isNewContractVersion
} from '../../history/presentation';
import ContractChangesModal from '../ContractChangesModal/ContractChangesModal';
import { loadContractHistory } from '../../services/ContractHistoryService';
import './ContractRecordInformation.less';

let nextPopoverId = 0;
const creationReasonLabels = new Map<SessionCreationReason, string>([
    ['ReRunAnalysis', 'Re-run analysis'],
    ['LayoutChanged', 'Layout changed']
]);

const ContractRecordInformation: React.FC = () => {
    const { sessionStore, rootStore } = useAppStore();
    const audit = sessionStore.response?.audit;
    const projectsStore = rootStore.projectsStore;
    const record = audit?.recordInformation;
    const updated = contractLastUpdated(audit);
    const isNewVersion = isNewContractVersion(audit);
    const [historyActors, setHistoryActors] = React.useState<string[]>([]);
    const loadPage = React.useCallback(
        (page: number, ascending: boolean, throughSequence?: number) =>
            loadContractHistory(sessionStore.sessionId, page, ascending, throughSequence),
        [sessionStore.sessionId]
    );
    const [popoverVisible, setPopoverVisible] = React.useState(false);
    const [historyVisible, setHistoryVisible] = React.useState(false);
    const [popoverId] = React.useState(() => `contract-record-information-${++nextPopoverId}`);
    const infoButton = React.useRef<HTMLButtonElement>(null);
    const popoverContent = React.useRef<HTMLDivElement>(null);

    // The shared lookup deduplicates missing IDs and batches requests. No archive
    // scan, current-viewer fallback or per-row network request is needed.
    const actorIds = [
        ...new Set(
            [record?.created?.userId, record?.lastSaved?.userId, ...historyActors].filter((id): id is string => !!id)
        )
    ].sort();
    const lookupKey = JSON.stringify(actorIds);
    // Read each observable name here: AntD invokes cell renderers outside this
    // observer. A snapshot makes later lookup batches refresh historical rows too.
    const names = Object.fromEntries(actorIds.map(id => [id, projectsStore.allUsers[id]]));
    React.useEffect(() => {
        projectsStore.loadUserDisplayNames(JSON.parse(lookupKey)).catch(() => undefined);
    }, [projectsStore, lookupKey]);

    const actorTimestamp = (fact?: ContractActorTimestampDto | null) =>
        fact ? `${contractActorName(fact.userId, names)} · ${formatContractTimestamp(fact.at)}` : 'Unavailable';
    const focusTrigger = () => infoButton.current?.focus();
    const predecessor = record?.createdFrom;
    const reason = (predecessor?.reason && creationReasonLabels.get(predecessor.reason)) || 'Unavailable';

    return (
        <span className="contract-record-information">
            <span className="contract-record-label">
                {isNewVersion ? 'Created' : 'Updated'} {formatContractTimestamp(updated?.at)}
            </span>
            <Popover
                trigger="click"
                visible={popoverVisible}
                onVisibleChange={setPopoverVisible}
                afterVisibleChange={visible => {
                    if (visible) {
                        popoverContent.current?.focus();
                    }
                }}
                title="Record information"
                content={
                    <div
                        ref={popoverContent}
                        tabIndex={-1}
                        role="region"
                        aria-label="Record information"
                        id={popoverId}
                        className="contract-record-popover"
                        onKeyDown={event => {
                            if (event.key === 'Escape') {
                                event.stopPropagation();
                                setPopoverVisible(false);
                                focusTrigger();
                            }
                        }}
                    >
                        <dl>
                            <dt>Created</dt>
                            <dd>{actorTimestamp(record?.created)}</dd>
                            <dt>Last updated</dt>
                            <dd>{actorTimestamp(updated)}</dd>
                            {predecessor?.sessionId && (
                                <>
                                    <dt>Created from</dt>
                                    <dd>
                                        <Link
                                            to={`/sessions/${encodeURIComponent(predecessor.sessionId)}`}
                                            onClick={() => setPopoverVisible(false)}
                                        >
                                            Previous analysis · {formatContractTimestamp(predecessor.createdAt)}
                                        </Link>
                                        <div>{reason}</div>
                                    </dd>
                                </>
                            )}
                        </dl>
                        <button
                            type="button"
                            className="contract-view-history"
                            onClick={() => {
                                setPopoverVisible(false);
                                setHistoryVisible(true);
                            }}
                        >
                            View history
                        </button>
                    </div>
                }
            >
                <button
                    ref={infoButton}
                    type="button"
                    className="contract-record-info-button"
                    aria-label="Record information"
                    aria-expanded={popoverVisible}
                    aria-controls={popoverVisible ? popoverId : undefined}
                    onKeyDown={event => {
                        if (event.key === 'Escape') {
                            setPopoverVisible(false);
                        }
                    }}
                >
                    <InfoCircleOutlined />
                </button>
            </Popover>
            <ContractChangesModal
                audit={audit}
                names={names}
                visible={historyVisible}
                onClose={() => setHistoryVisible(false)}
                afterClose={focusTrigger}
                loadPage={loadPage}
                onPageActors={setHistoryActors}
            />
        </span>
    );
};

export default observer(ContractRecordInformation);
